import * as React from "react";
import { Link, useLocation } from "react-router-dom";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import { alpha } from "@mui/material/styles";
import { FaPenNib } from "react-icons/fa";
import { useNotify } from "../hooks/NotifyProvider";
import { Login } from "./Login";
import {
  useHasUnreadNotification,
  useMarkNotificationsAsDoneMutation,
} from "../hooks/useNotification";
import { useContext, useEffect, useState } from "react";
import { CustomDialog } from "../ui/CustomDialog";
import LoginRoundedIcon from "@mui/icons-material/LoginRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import UserContext from "./UserProvider";
import { useNavigate } from "react-router-dom";
import InfoIcon from "@mui/icons-material/Info";
import LightModeRoundedIcon from "@mui/icons-material/LightModeRounded";
import DarkModeRoundedIcon from "@mui/icons-material/DarkModeRounded";
import NotificationsRoundedIcon from "@mui/icons-material/NotificationsRounded";
import NotificationsNoneRoundedIcon from "@mui/icons-material/NotificationsNoneRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { useRequireLogin } from "../hooks/useRequireLogin";
import { useThemeMode } from "../hooks/ThemeModeProvider";
import { IOSSwitch } from "../ui/IOSSwitch";
import { motion } from "framer-motion";
import {
  ListItemIcon,
  Stack,
  Menu,
  MenuItem,
  IconButton,
  Badge,
  Box,
} from "@mui/material";

export const Header = () => {
  const { user, logout } = useContext(UserContext);
  const [anchor, setAnchor] = useState(null);
  const [open, setOpen] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isNotificationActive = location.pathname === "/notifications";
  const goToCommunity = () => navigate("/");
  const hasUnread = useHasUnreadNotification(user?.userId);
  const markAsDoneMutation = useMarkNotificationsAsDoneMutation(user?.userId);
  const { isLoggedIn, LoginDialog, showLoginDialog } = useRequireLogin();
  const { notify } = useNotify();
  const { resolvedMode, setMode } = useThemeMode();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 8);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleClick = (e) => {
    setAnchor(e.currentTarget);
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };
  const handleInfo = () => {
    navigate("/information");
    handleClose();
  };
  const handleLogin = () => {
    setShowLogin(true);
  };
  const handleLoginClose = () => {
    setShowLogin(false);
  };
  const handleLogout = () => {
    if (!window.confirm("Log out?")) return;
    logout();
    goToCommunity();
    handleClose();
    notify("Logged out successfully.", "success");
  };
  const handlePostClick = () => {
    if (isLoggedIn()) {
      navigate("/PostRegister");
      handleClose();
    }
  };
  const handleNotificationClick = () => {
    if (isLoggedIn()) {
      navigate("/notifications");
      markAsDoneMutation.mutate();
    }
  };
  return (
    <div>
      <CustomDialog
        open={showLogin}
        title="Login"
        onClose={handleLoginClose}
        width="400px"
      >
        <Login
          updated={() => {
            handleClose();
            handleLoginClose();
          }}
        />
      </CustomDialog>
      {showLoginDialog && <LoginDialog />}
      <AppBar
        position="fixed"
        elevation={0}
        sx={(theme) => ({
          height: 65,
          bgcolor: scrolled
            ? alpha(theme.palette.background.default, 0.72)
            : "background.default",
          backdropFilter: scrolled ? "blur(16px)" : "none",
          WebkitBackdropFilter: scrolled ? "blur(16px)" : "none",
          transition:
            "background-color 0.2s ease, backdrop-filter 0.2s ease",
          color: "text.primary",
          alignSelf: "flex-start",
        })}
      >
        <Toolbar
          variant="dense"
          sx={{
            display: "flex",
            minHeight: "48px",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: "1000px",
              px: 1,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div className="mt-2">
              <Link to="/">
                <img
                  src="/rmr_logo.png"
                  alt="rmr Logo"
                  className="select-none h-[108px] md:h-[120px] w-auto object-contain"
                  style={{
                    // ロゴは黒一色の透過PNGのため、dark時はinvertで白ロゴ相当にする
                    filter: resolvedMode === "dark" ? "invert(1)" : "none",
                  }}
                />
              </Link>
            </div>
            <div className="">
              <Stack
                direction="row"
                sx={{
                  gap: 1,
                  alignItems: "center",
                }}
              >
                <motion.div className="hidden md:block" whileTap={{ scale: 0.9 }}>
                  <IconButton onClick={handlePostClick}>
                    <FaPenNib size="24px" />
                  </IconButton>
                </motion.div>
                <motion.div whileTap={{ scale: 0.9 }}>
                  <IconButton onClick={handleNotificationClick}>
                    <Badge color="warning" variant="dot" invisible={!hasUnread}>
                      {isNotificationActive ? (
                        <NotificationsRoundedIcon />
                      ) : (
                        <NotificationsNoneRoundedIcon />
                      )}
                    </Badge>
                  </IconButton>
                </motion.div>
                <motion.div whileTap={{ scale: 0.9 }}>
                  <IconButton
                    aria-label="settings"
                    id="long-button"
                    aria-haspopup="true"
                    onClick={handleClick}
                  >
                    {open ? <SettingsRoundedIcon /> : <SettingsOutlinedIcon />}
                  </IconButton>
                </motion.div>
                <Menu anchorEl={anchor} open={open} onClose={handleClose}>
                  <MenuItem
                    onClick={handleInfo}
                    sx={{
                      textDecoration: "none",
                      py: 1.2,
                      "&:hover": {
                        textDecoration: "none",
                      },
                    }}
                  >
                    <ListItemIcon>
                      <InfoIcon />
                    </ListItemIcon>
                    <div className="font-soft">About</div>
                  </MenuItem>
                  <MenuItem
                    sx={{
                      py: 1.2,
                      justifyContent: "center",
                      gap: 1,
                      "&:hover": {
                        backgroundColor: "transparent",
                      },
                    }}
                  >
                    <LightModeRoundedIcon
                      fontSize="small"
                      sx={{
                        color:
                          resolvedMode === "dark"
                            ? "text.disabled"
                            : "warning.main",
                      }}
                    />
                    <IOSSwitch
                      checked={resolvedMode === "dark"}
                      onChange={(_, checked) =>
                        setMode(checked ? "dark" : "light")
                      }
                      inputProps={{ "aria-label": "Toggle dark mode" }}
                    />
                    <DarkModeRoundedIcon
                      fontSize="small"
                      sx={{
                        color:
                          resolvedMode === "dark"
                            ? "primary.light"
                            : "text.disabled",
                      }}
                    />
                  </MenuItem>
                  {user ? (
                    <MenuItem
                      onClick={() => handleLogout()}
                      sx={{
                        textDecoration: "none",
                        py: 1.2,
                        "&:hover": {
                          textDecoration: "none",
                        },
                      }}
                    >
                      <ListItemIcon>
                        <LogoutRoundedIcon />
                      </ListItemIcon>
                      <div className="font-soft">Log Out</div>
                    </MenuItem>
                  ) : (
                    <MenuItem
                      onClick={() => handleLogin()}
                      sx={{
                        textDecoration: "none",
                        py: 1.2,
                        "&:hover": {
                          textDecoration: "none",
                        },
                      }}
                    >
                      <ListItemIcon>
                        <LoginRoundedIcon />
                      </ListItemIcon>
                      <div className="font-soft">Log In</div>
                    </MenuItem>
                  )}
                </Menu>
              </Stack>
            </div>
          </Box>
        </Toolbar>
      </AppBar>
    </div>
  );
};
